import React from 'react';
import { ChecklistType, CHECKLIST_CONFIGS, InfoPrincipali } from '../../types/datamodel';
import { CheckCircle, XCircle } from 'lucide-react';

interface ParteAFormProps {
  data: any; // Use any to avoid TypeScript issues with partial data
  infoPrincipali: InfoPrincipali;
  checklistType: ChecklistType;
  onChange: (data: any) => void;
}

export function ParteAForm({ data, infoPrincipali, checklistType, onChange }: ParteAFormProps) {
  const config = CHECKLIST_CONFIGS[checklistType];

  // Ensure data is always an object
  const safeData = data || {};

  const handleChange = (section: string, field: string, value: any) => {
    const currentSection = safeData[section] || {};
    const newData = {
      ...safeData,
      [section]: {
        ...currentSection,
        [field]: value
      }
    };
    console.log('🔄 ParteAForm handleChange:', { section, field, value, newData });
    onChange(newData);
  };

  // Helper per formattare una data in formato yyyy-MM-dd per input type="date"
  const formatDateForInput = (date: any): string => {
    if (!date) return '';

    // Se è già una stringa, controlla se è in formato ISO
    if (typeof date === 'string') {
      // Se è ISO format, estrai solo la parte della data
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      // Altrimenti prova a creare un oggetto Date e formattarlo
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        return d.toISOString().split('T')[0];
      }
      return '';
    }

    // Se è un oggetto Date
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }

    return '';
  };

  const renderEsito = (esito: string) => {
    if (esito === 'CONFORME') {
      return (
        <div className="flex items-center text-green-600 font-semibold">
          <CheckCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    if (esito === 'NON CONFORME') {
      return (
        <div className="flex items-center text-red-600 font-semibold">
          <XCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* A1 - Modalità di pagamento dei premi */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">A1 - Modalità di Pagamento dei Premi</h3>
          {renderEsito(safeData.a1?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Incasso
            </label>
            <input
              type="date"
              value={formatDateForInput(safeData.a1?.dataIncasso)}
              onChange={(e) => handleChange('a1', 'dataIncasso', e.target.value ? e.target.value : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Corretto Addebito <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.a1?.correttoAddebito || ''}
              onChange={(e) => handleChange('a1', 'correttoAddebito', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.a1?.note || ''}
              onChange={(e) => handleChange('a1', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>

      {/* A2 - Consegna MUP */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">A2 - Consegna MUP</h3>
          {renderEsito(safeData.a2?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attestazione Consegna MUP <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.a2?.attestazioneConsegnaMUP || ''}
              onChange={(e) => handleChange('a2', 'attestazioneConsegnaMUP', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.a2?.note || ''}
              onChange={(e) => handleChange('a2', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>

      {/* A3 - Consegna informativa precontrattuale */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">A3 - Consegna Informativa Precontrattuale</h3>
          {renderEsito(safeData.a3?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presenza Proposta/Contratto <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.a3?.presenzaPropostaContratto || ''}
              onChange={(e) => handleChange('a3', 'presenzaPropostaContratto', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presenza Firma Proposta <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.a3?.presenzaFirmaProposta || ''}
              onChange={(e) => handleChange('a3', 'presenzaFirmaProposta', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>

          {/* Campi condizionali basati su tipologia operazione */}
          {infoPrincipali.tipologiaOperazione === 'Versamenti aggiuntivi' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Richiesta Variazione Versamento
                </label>
                <select
                  value={safeData.a3?.presenzaRichiestaVariazioneVers || ''}
                  onChange={(e) => handleChange('a3', 'presenzaRichiestaVariazioneVers', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firma Richiesta Versamento
                </label>
                <select
                  value={safeData.a3?.presenzaFirmaRichiestaVers || ''}
                  onChange={(e) => handleChange('a3', 'presenzaFirmaRichiestaVers', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
            </>
          )}

          {infoPrincipali.tipologiaOperazione === 'Riscatti/liquidazioni' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Richiesta Prestazione
                </label>
                <select
                  value={safeData.a3?.presenzaRichiestaPrestazione || ''}
                  onChange={(e) => handleChange('a3', 'presenzaRichiestaPrestazione', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firma Prestazione
                </label>
                <select
                  value={safeData.a3?.presenzaFirmaPrestazione || ''}
                  onChange={(e) => handleChange('a3', 'presenzaFirmaPrestazione', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
            </>
          )}

          {infoPrincipali.tipologiaOperazione === 'Variazione contraente' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Richiesta Variazione Contraente
                </label>
                <select
                  value={safeData.a3?.presenzaRichiestaVariazioneCont || ''}
                  onChange={(e) => handleChange('a3', 'presenzaRichiestaVariazioneCont', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firma Variazione Contraente
                </label>
                <select
                  value={safeData.a3?.presenzaFirmaVariazioneCont || ''}
                  onChange={(e) => handleChange('a3', 'presenzaFirmaVariazioneCont', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleziona...</option>
                  <option value="SI">SI</option>
                  <option value="NO">NO</option>
                </select>
              </div>
            </>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.a3?.note || ''}
              onChange={(e) => handleChange('a3', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>

      {/* A4 - Rispetto adeguatezza contratti (solo IBIP) */}
      {config.hasA4 && safeData.a4 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">A4 - Rispetto Adeguatezza Contratti</h3>
            {renderEsito(safeData.a4?.esito || '')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenza Questionario <span className="text-red-500">*</span>
              </label>
              <select
                value={safeData.a4?.presenzaQuestionario || ''}
                onChange={(e) => handleChange('a4', 'presenzaQuestionario', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenza Firma Questionario <span className="text-red-500">*</span>
              </label>
              <select
                value={safeData.a4?.presenzaFirmaQuestionario || ''}
                onChange={(e) => handleChange('a4', 'presenzaFirmaQuestionario', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                value={safeData.a4?.note || ''}
                onChange={(e) => handleChange('a4', 'note', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
        </div>
      )}

      {/* A5 - Materiale pubblicitario */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">A5 - Materiale Pubblicitario</h3>
          {renderEsito(safeData.a5?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Materiale Personalizzato <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.a5?.materialePersonalizzato || ''}
              onChange={(e) => handleChange('a5', 'materialePersonalizzato', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
              <option value="N/A">N/A</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.a5?.note || ''}
              onChange={(e) => handleChange('a5', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>

      {/* A6 - Siti internet intermediari (solo IBIP e Protection) */}
      {config.hasA6 && safeData.a6 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">A6 - Siti Internet Intermediari</h3>
            {renderEsito(safeData.a6?.esito || '')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aggiornamento Set Informativo
              </label>
              <select
                value={safeData.a6?.aggiornamentoSetInformativo || ''}
                onChange={(e) => handleChange('a6', 'aggiornamentoSetInformativo', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
                <option value="NA">NA</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenza Set sul Sito
              </label>
              <select
                value={safeData.a6?.presenzaSetSito || ''}
                onChange={(e) => handleChange('a6', 'presenzaSetSito', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
                <option value="NA">NA</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                value={safeData.a6?.note || ''}
                onChange={(e) => handleChange('a6', 'note', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
